import {compact, isEqual, noop, partition, uniqBy} from 'lodash';
import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'styled-components';

import {getWidthOfElement} from '../../../../../libs/shared/core/src/helpers/browser/domHelpers';
import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {getHasPreventPinTeamTags} from '../../../../../libs/shared/core/src/store/featureFlags/featureFlagsSelectors';
import {CellsContext} from '../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {InboxesPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {VisuallyHidden} from '../../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {useSize} from '../../../../../libs/shared/ui/src/hooks/sizeHooks';
import {AppSelector, createSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {selectedSearchFilterKey} from '../../../../../srcCommon/inboxes/search/searchFiltersHelpers';
import {ModifiersEnum} from '../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {
  buildSearchQueryFromParts,
  isModifierQueryPart,
  removeModifierValueFromParts,
  splitSearchQuery,
  toggleModifierValue,
} from '../../../../../srcCommon/inboxes/search/searchQueryHelpers';
import {useAppDispatch, useAppSelector} from '../../../../helpers/appReduxHelpers';
import {
  isSearchStaticModifierHintType,
  SearchHintModel,
  SearchModifierHintTypesEnum,
  SearchStaticHintTypesEnum,
  SearchStaticModifierHintTypesEnum,
} from '../../../../models/api/searchHintModel';
import {TagModel} from '../../../../models/api/tagModel';
import {InboxesSearchLocationsEnum} from '../../../../models/url/models/inboxesUrlModel';
import {getInboxes} from '../../../../store/data/inboxes/inboxesSelectors';
import {getLenses} from '../../../../store/data/lenses/lensesSelectors';
import {updateSelectedFilterAction} from '../../../../store/data/localPreferences/localPreferencesActions';
import {getStarredTagIds, getTags} from '../../../../store/data/tags/tagsSelectors';
import {getFindTeammate} from '../../../../store/data/teammates/teammatesSelectors';
import {getTeams} from '../../../../store/data/teams/teamsSelectors';
import {CellsPanelPageTypesEnum} from '../../../../store/panels/cells/cellsPanelState';
import {maybeUpdateSearchSessionTimestampAction} from '../../../../store/panels/cells/search/cellsPanelSearchActions';
import {
  getCellsSearchHints,
  getCellsSearchQueryHash,
} from '../../../../store/panels/cells/search/cellsPanelSearchSelectors';
import {toggleSearchQueryBuilderDropdownAction} from '../../../../store/panels/inboxes/inboxesPanelActions';
import {InboxesConversationsParametersWithSearch} from '../../../../store/panels/inboxes/parameters/inboxesParameters';
import {buildParametersSearchLocalFilterName} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersHelpers';
import {
  getParametersSearchLocalFilterIcon,
  SearchLocalFilterIconType,
} from '../../../../store/panels/inboxes/parameters/inboxesParametersFiltersIconHelpers';
import {getFirstInboxesCellsPanel} from '../../../../store/panels/panelsSelectors';
import {searchByParametersAction} from '../../../../store/stateless/searchActions';
import {searchScopeDropdownItems} from '../searchBar/queryBuilder/inboxesSearchQueryBuilderHelpers';
import {SearchHintClickTrackingOptions, trackSearchHintClick} from '../searchTrackingHelpers';
import {SearchHintContact} from './searchHintContact/searchHintContact';
import {SearchHintDate} from './searchHintDate/searchHintDate';
import {SearchHintIn} from './searchHintIn/searchHintIn';
import {SearchModifierStatus} from './searchHintIs/searchModifierStatus';
import {SearchHintItem} from './searchHintItem';
import {
  makeSearchFiltersVisualHint,
  maybeWrapInDoubleQuotes,
  serializeToModifierVisualHint,
} from './searchHintsHelpers';
import {VisualLocationSearchHint, VisualModifierSearchHint} from './searchHintsTypes';
import {SearchHintTagItem} from './searchHintTag/searchHintTagItem';
import {getStatusModifierValues, useSearchCardsFromQuery, useTagsFromQuery} from './searchModifiersHooks';

/*
 * Constants.
 */

const SEARCH_HINT_BAR_PADDING = 8;
const SEARCH_HINT_GAP = 8;

/*
 * Props.
 */

interface SearchHintsBarProps {
  inboxesPanelId: InboxesPanelId;
  parameters: InboxesConversationsParametersWithSearch;
}

/*
 * Styles.
 */

const StyledButtonWrapper = styled.div``;

const StyledSearchHintsBarWrapper = styled.div`
  display: flex;
  // Padding bottom is 8px because we have a 1px border.
  padding: ${SEARCH_HINT_BAR_PADDING}px;

  background-color: ${(p) => p.theme.foreground};

  > *:not(:first-child) {
    margin-left: ${SEARCH_HINT_GAP}px;
  }

  ${StyledButtonWrapper} {
    margin-left: auto;
  }
  grid-area: filters;

  width: 100%;
  padding-left: 14px;
  overflow: hidden;
`;

const StyledVisuallyHidden = styled(VisuallyHidden)`
  display: flex;
`;

/*
 * Component.
 */

export const SearchHintsBar: FC<PropsWithChildren<SearchHintsBarProps>> = ({inboxesPanelId, parameters}) => {
  const [getSearchLocalFilterName, getSearchLocalFilterIcon] = useMemo(
    () => [makeGetSearchLocalFilterName(parameters), makeGetSearchLocalFilterIcon(parameters)],
    [parameters],
  );
  const hasPreventPinTeamTags = useAppSelector(getHasPreventPinTeamTags);
  const localSearchFilterName = useAppSelector(getSearchLocalFilterName);
  const localSearchFilterIcon = useAppSelector(getSearchLocalFilterIcon);

  const [cellsContext, searchHints, isLoading] = useAppSelector(getSearchHintsStateProps);
  const queryHash = useAppSelector(getCellsSearchQueryHash);

  const {
    search: {query: searchQuery},
  } = parameters;

  const dispatch = useAppDispatch();

  const localize = useLocalize();

  const selectedTags = useTagsFromQuery(searchQuery);

  const computedSearchHints: VisualSearchHints = useMemo(
    () =>
      computeVisualSearchHints(
        localize,
        parameters,
        localSearchFilterName,
        localSearchFilterIcon,
        searchHints,
        {
          hasPreventPinTeamTags,
          isTagSelected: selectedTags.length > 0,
        },
      ),
    [
      localize,
      parameters,
      localSearchFilterName,
      localSearchFilterIcon,
      searchHints,
      hasPreventPinTeamTags,
      selectedTags,
    ],
  );

  // Always display the search filters hint at the beginning.
  const [displayedHints, setDisplayedHints] = useState<VisualSearchHints>([computedSearchHints[0]]);

  const searchHintsBarWrapperRef = useRef<HTMLDivElement>(null);
  const searchHintsBarWrapperSizeInfo = useSize(searchHintsBarWrapperRef);
  // Because searchHintsBarWrapperRef is a ref, we can't detect it changing.
  // We can make this a function that will calculate the width of the wrapper on each call.
  const searchHintsBarWrapperWidth = useCallback(() => {
    const parent = searchHintsBarWrapperRef.current?.parentElement;
    if (!parent) {
      return searchHintsBarWrapperSizeInfo?.width || 0;
    }
    const actionBar = parent.children[1];
    if (!actionBar) {
      return searchHintsBarWrapperSizeInfo?.width || 0;
    }
    const availableWidth = parent.clientWidth - actionBar.clientWidth;
    return Math.min(availableWidth, searchHintsBarWrapperSizeInfo?.width || Infinity);
  }, [searchHintsBarWrapperRef, searchHintsBarWrapperSizeInfo]);
  const invisibleRenderRef = useRef<HTMLDivElement>(null);

  // We may need to remove some search hints if the width is too small.
  useLayoutEffect(() => {
    if (!invisibleRenderRef.current) {
      return;
    }

    // Subtract the horizontal padding.
    const availableWidth = searchHintsBarWrapperWidth() - 2 * SEARCH_HINT_BAR_PADDING;

    const rootElement = invisibleRenderRef.current;
    const children = Array.from(rootElement.children);
    const childrenWidths = children.map((e, index) => ({
      index,
      width: Math.ceil(getWidthOfElement(e)),
    }));
    const childrenTotalWidth = childrenWidths.reduce(
      (acc, curr, index) => acc + curr.width + (index > 0 ? SEARCH_HINT_GAP : 0),
      0,
    );

    // If we have enough width, render all the computed hints.
    if (childrenTotalWidth <= availableWidth) {
      // Avoid a cascading render.
      setDisplayedHints((hints) => {
        if (isEqual(hints, computedSearchHints)) {
          return hints;
        }

        return computedSearchHints;
      });
    } else if (childrenWidths.length > 0) {
      // The last child is the "More filters" button, which should always be visible.
      const moreFiltersButtonWidth = childrenWidths[childrenWidths.length - 1];
      // Subtract the width of the "More filters" button.
      const availableHintsWidth = availableWidth - (moreFiltersButtonWidth.width + SEARCH_HINT_GAP);

      const [searchFilterHintWidth, ...otherChildrenWidths] = childrenWidths;
      const [searchFilterHint, ...otherHints] = computedSearchHints;

      let hintsCurrentWidth = searchFilterHintWidth.width;
      const hintsToBeDisplayed: VisualSearchHints = [searchFilterHint];

      for (const [index, hint] of otherHints.entries()) {
        const {width} = otherChildrenWidths[index];

        // Update the width of displayed hints by adding the current hint width.
        hintsCurrentWidth += width + SEARCH_HINT_GAP;

        // If the current width is above the available one, break.
        if (hintsCurrentWidth > availableHintsWidth) {
          break;
        }

        // Otherwise, display the current hint.
        hintsToBeDisplayed.push(hint);
      }

      // Avoid a cascading render.
      setDisplayedHints((hints) => {
        if (isEqual(hints, hintsToBeDisplayed)) {
          return hints;
        }

        return hintsToBeDisplayed;
      });
    }
  }, [computedSearchHints, searchHintsBarWrapperWidth]);

  const onLocationHintClick = (location: InboxesSearchLocationsEnum) => {
    // Maybe update search session timestamp.
    // It would be undefined if the user did not interact with the search bar before.
    dispatch(maybeUpdateSearchSessionTimestampAction());

    // If `location` changed, save it in localStorage.
    dispatch(updateSelectedFilterAction(selectedSearchFilterKey, location));

    // Track.
    trackSearchHintClick({
      type: SearchStaticHintTypesEnum.SEARCH_FILTERS,
      queryHash,
      // The has:no-calendar hint is a modifier hint.
      isHasNoCalendar: false,
      // We did not change the query, so `inputQuery` and `outputQuery` will be the same.
      inputQuery: searchQuery,
      outputQuery: searchQuery,
    });

    const updatedSearchParameters = {
      ...parameters.search,
      location,
    };

    // Navigate to search with updated search parameters.
    dispatch(
      searchByParametersAction(updatedSearchParameters, cellsContext, {
        isFolderSwitch: true,
      }),
    );
  };

  /**
   * When this method is called, the values are the ones when the click was made.
   * Thus, if an user clicked on the hint to enable "Has attachment" for example, `valueWasSelected` is still falsy here.
   * If a hint is transient, it means we're not keeping the value in the search bar if it's disabled.
   */
  const onModifierHintClick = (
    modifier: ModifiersEnum,
    value: string,
    trackingOptions: Pick<SearchHintClickTrackingOptions, 'type'>,
  ) => {
    // Maybe update search session timestamp.
    // It would be undefined if the user did not interact with the search bar before.
    dispatch(maybeUpdateSearchSessionTimestampAction());
    const enabledText = `${modifier}:${maybeWrapInDoubleQuotes(value)}`;

    const queryParts = splitSearchQuery(searchQuery);
    const {queryParts: updatedQueryParts, valueWasSelected} = toggleModifierValue(
      queryParts,
      modifier,
      value,
    );

    const trimmedUpdatedQuery = buildSearchQueryFromParts(updatedQueryParts);
    // Track.
    const isHasNoCalendar = enabledText === 'has:no-calendar';

    trackSearchHintClick({
      type: trackingOptions.type,
      shouldEnrichQuery: !valueWasSelected,
      queryHash,
      isHasNoCalendar,
      inputQuery: searchQuery,
      outputQuery: trimmedUpdatedQuery,
      modifier,
    });

    // Navigate to search with updated query.
    dispatch(
      searchByParametersAction(
        trimmedUpdatedQuery.length === 0
          ? undefined
          : {
              ...parameters.search,
              query: trimmedUpdatedQuery,
            },
        cellsContext,
      ),
    );
  };

  const onClearModifierHint = (
    modifier: ModifiersEnum,
    trackingOptions: Pick<SearchHintClickTrackingOptions, 'type'>,
  ) => {
    dispatch(maybeUpdateSearchSessionTimestampAction());
    const queryParts = splitSearchQuery(searchQuery);
    const updatedQueryParts = queryParts.filter((e) => !isModifierQueryPart(e, modifier));
    const updatedQuery = buildSearchQueryFromParts(updatedQueryParts);

    trackSearchHintClick({
      type: trackingOptions.type,
      shouldEnrichQuery: true,
      queryHash,
      isHasNoCalendar: false,
      inputQuery: searchQuery,
      outputQuery: updatedQuery,
      modifier,
    });

    dispatch(
      searchByParametersAction(
        updatedQuery.length === 0
          ? undefined
          : {
              ...parameters.search,
              query: updatedQuery,
            },
        cellsContext,
      ),
    );
  };
  const onMoreFiltersClick: () => void = () => {
    // Maybe update search session timestamp.
    // It would be undefined if the user did not interact with the search bar before.
    dispatch(maybeUpdateSearchSessionTimestampAction());

    // Open the search query builder dropdown.
    dispatch(toggleSearchQueryBuilderDropdownAction(inboxesPanelId, 'hints-bar'));
  };

  const trackDateHintChange = (trimmedUpdatedQuery: string, modifier: ModifiersEnum | 'before_after') => {
    trackSearchHintClick({
      type: modifier,
      shouldEnrichQuery: true,
      queryHash,
      isHasNoCalendar: false,
      inputQuery: searchQuery,
      outputQuery: trimmedUpdatedQuery,
      modifier,
    });
  };

  const selectedStatusValues = getStatusModifierValues(searchQuery);

  const onClickIsFilter = (status: string) => {
    onModifierHintClick(ModifiersEnum.IS, status, {
      type: ModifiersEnum.IS,
    });
  };

  const onClearIsFilter = () => onClearModifierHint(ModifiersEnum.IS, {type: ModifiersEnum.IS});

  const onClickTagsFilter = (newTags: TagModel[]) => {
    dispatch(maybeUpdateSearchSessionTimestampAction());

    const queryPartsWihoutTags = removeModifierValueFromParts(
      splitSearchQuery(searchQuery),
      ModifiersEnum.TAG,
    );

    let newQuery = buildSearchQueryFromParts(queryPartsWihoutTags);

    uniqBy(newTags, (e) => e.alias)
      .map((e) => e.alias)
      .forEach((alias) => {
        const {queryParts} = toggleModifierValue(splitSearchQuery(newQuery), ModifiersEnum.TAG, alias);
        newQuery = buildSearchQueryFromParts(queryParts);
      });

    trackSearchHintClick({
      type: ModifiersEnum.TAG,
      shouldEnrichQuery: false,
      queryHash,
      isHasNoCalendar: false,
      inputQuery: searchQuery,
      outputQuery: newQuery,
      modifier: ModifiersEnum.TAG,
    });

    // Navigate to search with updated query.
    dispatch(
      searchByParametersAction(
        newQuery.length === 0
          ? undefined
          : {
              ...parameters.search,
              query: newQuery,
            },
        cellsContext,
      ),
    );
  };
  const fromContacts = useSearchCardsFromQuery(searchQuery, ModifiersEnum.FROM);
  const toContacts = useSearchCardsFromQuery(searchQuery, ModifiersEnum.TO);

  const getInitialContactSuggestions = useCallback(
    (hint: VisualModifierSearchHint) =>
      uniqBy(
        hint.suggestedValues?.map((e) => ({
          name: e.title,
          handle: e.value,
        })),
        'handle',
      ),
    [],
  );

  const onClickContactFilterBuilder =
    (modifier: ModifiersEnum.FROM | ModifiersEnum.TO) => (handle: string) => {
      onModifierHintClick(modifier, handle, {
        type: modifier,
      });
    };

  const onClearContactFilterBuilder = (modifier: ModifiersEnum.FROM | ModifiersEnum.TO) => () =>
    onClearModifierHint(modifier, {type: modifier});

  const renderHints = (hints: VisualSearchHints) => {
    const [locationHint, ...otherHints] = hints;

    const wrappedOnModifierHintClick = (hint: VisualModifierSearchHint, valueIndex: number) => {
      const hintValue = hint.values[valueIndex];
      const {modifier, value} = hintValue;
      onModifierHintClick(modifier, value, {type: hint.type});
    };

    const renderSearchHintItems = () => (
      <>
        {otherHints.map((hint) => {
          switch (hint.type) {
            case SearchStaticModifierHintTypesEnum.FROM:
            case SearchStaticModifierHintTypesEnum.TO: {
              const modifier =
                hint.type === SearchStaticModifierHintTypesEnum.FROM ? ModifiersEnum.FROM : ModifiersEnum.TO;

              return (
                <SearchHintContact
                  key={hint.key}
                  selectedContacts={
                    hint.type === SearchStaticModifierHintTypesEnum.FROM ? fromContacts : toContacts
                  }
                  isDisabled={isLoading}
                  onClick={isLoading ? noop : onClickContactFilterBuilder(modifier)}
                  onClear={isLoading ? noop : onClearContactFilterBuilder(modifier)}
                  cellsContext={cellsContext}
                  initialSuggestions={getInitialContactSuggestions(hint)}
                  queryHash={queryHash}
                  modifier={modifier}
                />
              );
            }

            case SearchStaticModifierHintTypesEnum.TAG:
              return (
                <SearchHintTagItem
                  key={hint.key}
                  isDisabled={isLoading}
                  queryHash={queryHash}
                  selectedTags={selectedTags}
                  onSelectedTagsChange={isLoading ? noop : onClickTagsFilter}
                />
              );

            case SearchStaticModifierHintTypesEnum.IN:
              return (
                <SearchHintIn
                  key={hint.key}
                  isDisabled={isLoading}
                  onClick={isLoading ? noop : onModifierHintClick}
                  onClear={isLoading ? noop : onClearModifierHint}
                  queryHash={queryHash}
                  query={searchQuery}
                />
              );

            case SearchStaticModifierHintTypesEnum.IS:
              return (
                <SearchModifierStatus
                  key={hint.key}
                  selectedItems={selectedStatusValues}
                  isDisabled={isLoading}
                  onClick={isLoading ? noop : onClickIsFilter}
                  onClear={isLoading ? noop : onClearIsFilter}
                  queryHash={queryHash}
                />
              );

            case SearchStaticModifierHintTypesEnum.DATE:
              return (
                <SearchHintDate
                  key="date"
                  query={searchQuery}
                  isDisabled={isLoading}
                  trackDateHintChange={trackDateHintChange}
                  queryHash={queryHash}
                  onQueryChange={(newQuery) => {
                    dispatch(
                      searchByParametersAction(
                        newQuery.length === 0
                          ? undefined
                          : {
                              ...parameters.search,
                              query: newQuery,
                            },
                        cellsContext,
                      ),
                    );
                  }}
                />
              );
            default:
              return (
                <SearchHintItem
                  key={hint.key}
                  hint={hint}
                  isDisabled={isLoading}
                  onClick={
                    isLoading
                      ? noop
                      : // TODO(ldemarne): Cleanup once we settle on a model.
                        wrappedOnModifierHintClick
                  }
                />
              );
          }
        })}
      </>
    );

    return (
      <>
        <SearchHintItem
          hint={locationHint}
          isDisabled={isLoading}
          onClick={
            isLoading
              ? noop
              : (_, valueIndex) => {
                  // TODO(ldemarne): Cleanup once we settle on a model.
                  onLocationHintClick(locationHint.values[valueIndex].value);
                }
          }
        />
        {renderSearchHintItems()}
        {inboxesPanelId && !isLoading && (
          <Button
            variant={ButtonVariantsEnum.LINK}
            onClick={onMoreFiltersClick}
            interactionId="search-hint-more-filters"
          >
            <Trans id="search-hint-more-filters">More filters</Trans>
          </Button>
        )}
      </>
    );
  };

  return (
    <StyledSearchHintsBarWrapper id="tmp-pendo-new-search-filters" ref={searchHintsBarWrapperRef}>
      {renderHints(displayedHints)}
      <StyledVisuallyHidden ref={invisibleRenderRef}>{renderHints(computedSearchHints)}</StyledVisuallyHidden>
    </StyledSearchHintsBarWrapper>
  );
};

/*
 * Helpers.
 */

export function makeGetSearchLocalFilterName(inboxesParameters: InboxesConversationsParametersWithSearch) {
  return createSelector(
    [getInboxes, getTags, getLenses, getFindTeammate, getTeams],
    (inboxes, tags, lenses, findTeammate, teams) => {
      const options = {
        shouldWrapAllNamesInQuotes: false,
        shouldWrapProperNounsInQuotes: true,
      };

      return buildParametersSearchLocalFilterName(
        inboxes,
        tags,
        lenses,
        findTeammate,
        teams,
        inboxesParameters,
        options,
      );
    },
  );
}

function makeGetSearchLocalFilterIcon(inboxesParameters: InboxesConversationsParametersWithSearch) {
  return createSelector(
    [getInboxes, getTags, getStarredTagIds, getLenses, getTeams, getFindTeammate],
    (inboxes, tags, starredTagIds, lenses, teams, findTeammate) =>
      getParametersSearchLocalFilterIcon(
        inboxes,
        tags,
        lenses,
        starredTagIds,
        teams,
        findTeammate,
        inboxesParameters,
      ),
  );
}

const getSearchHintsStateProps: AppSelector<
  [CellsContext | undefined, ReadonlyArray<SearchHintModel> | undefined, boolean]
> = createSelector([getFirstInboxesCellsPanel, getCellsSearchHints], (panel, searchHints) => {
  if (!panel) {
    return [undefined, undefined, false];
  }

  const activePage = panel.pages.current || panel.pages.next;

  if (!activePage || activePage.type !== CellsPanelPageTypesEnum.CONVERSATIONS) {
    return [undefined, undefined, false];
  }

  const {isLoading, results} = searchHints || {};

  return [activePage.cellsContext, results || undefined, isLoading || false];
});

type VisualSearchHints = [VisualLocationSearchHint, ...ReadonlyArray<VisualModifierSearchHint>];

function computeVisualSearchHints(
  localize: Localizer,
  parameters: InboxesConversationsParametersWithSearch,
  localSearchFilterName: ReactNode,
  localSearchFilterIcon: SearchLocalFilterIconType,
  searchHints: ReadonlyArray<SearchHintModel> | undefined,
  options: {
    hasPreventPinTeamTags: boolean;
    isTagSelected: boolean;
  },
): VisualSearchHints {
  const {hasPreventPinTeamTags, isTagSelected} = options;

  const searchFiltersHint = makeSearchFiltersVisualHint(
    parameters.search.location,
    localSearchFilterName,
    localSearchFilterIcon,
    localize,
    hasPreventPinTeamTags,
  );

  const otherHints = compact(
    (searchHints || []).map((hint) => serializeToModifierVisualHint(hint, parameters.search, localize)),
  );

  const makeModifierHintPredicate = (modifier: ModifiersEnum) => (hint: VisualModifierSearchHint) =>
    hint.type === SearchModifierHintTypesEnum.MODIFIER && hint.values[0]?.modifier === modifier;

  const findHint = (modifier: ModifiersEnum) => otherHints.find(makeModifierHintPredicate(modifier));

  // This assumes there's no more than one modifier tag hint, which should be true.
  const selectedFromHint = findHint(ModifiersEnum.FROM);
  const selectedToHint = findHint(ModifiersEnum.TO);

  // The from hint should always be displayed even if not selected / recommended by the backend.
  // the hint.type === 'recipient' is a suggestion of a contact from the backend. We will add it to a list of initial suggestions.
  const recipientHint = otherHints.find((hint) => hint.type === SearchModifierHintTypesEnum.RECIPIENT);

  const fromHint = {
    type: SearchStaticModifierHintTypesEnum.FROM,
    values: selectedFromHint?.values ?? [],
    key: ModifiersEnum.FROM,
    isTransient: selectedFromHint?.isTransient ?? false,
    suggestedValues: recipientHint?.values ?? [],
  };

  const toHint = {
    type: SearchStaticModifierHintTypesEnum.TO,
    values: selectedToHint?.values ?? [],
    key: ModifiersEnum.TO,
    isTransient: selectedToHint?.isTransient ?? false,
    suggestedValues: recipientHint?.values ?? [],
  };

  const tagHint = {
    type: SearchStaticModifierHintTypesEnum.TAG,
    key: ModifiersEnum.TAG,
    values: [],
    isTransient: false,
  };

  const isHint = {
    type: SearchStaticModifierHintTypesEnum.IS,
    key: ModifiersEnum.IS,
    values: [],
    isTransient: false,
  };

  const inHint = {
    type: SearchStaticModifierHintTypesEnum.IN,
    key: ModifiersEnum.IN,
    values: [],
    isTransient: false,
  };

  const isHasAttachmentSelected = parameters.search.query.includes('has:attachments');

  const hasAttachmentsHint: VisualModifierSearchHint = {
    type: SearchModifierHintTypesEnum.MODIFIER,
    key: ModifiersEnum.HAS,
    values: [
      {
        key: 'has-attachments-0',
        value: 'attachments',
        modifier: ModifiersEnum.HAS,
        title: 'Has attachments',
        isSelected: isHasAttachmentSelected,
        icon: undefined,
        emoji: undefined,
      },
    ],
    isTransient: false,
  };

  const searchScopes = searchScopeDropdownItems();
  const isInSelected = parameters.search.query
    .split(' ')
    .some((e) => searchScopes.some((scope) => e === `in:${scope.inputValue.value}`));

  const isStatusSelected = getStatusModifierValues(parameters.search.query).length > 0;

  // The date hint values are not returned by the backend.
  const dateHint = {
    type: SearchStaticModifierHintTypesEnum.DATE,
    key: ModifiersEnum.CUSTOM_FIELD,
    values: [],
    isTransient: false,
  };

  // Remove the hints that were used to compute the new static hints.
  const filteredOtherHints = otherHints.filter((hint) => {
    const {type, values} = hint;
    const firstModifier = values[0]?.modifier;

    return !(
      type === SearchModifierHintTypesEnum.RECIPIENT ||
      (type === SearchModifierHintTypesEnum.MODIFIER && isSearchStaticModifierHintType(firstModifier)) ||
      hint.values.some((val) => val.value === 'attachments' && val.modifier === ModifiersEnum.HAS)
    );
  });

  const allHints: VisualModifierSearchHint[] = [
    fromHint,
    toHint,
    hasAttachmentsHint,
    isHint,
    dateHint,
    inHint,
    tagHint,
    ...filteredOtherHints,
  ];

  const isDateSelected =
    parameters.search.query.includes('before:') ||
    parameters.search.query.includes('after:') ||
    parameters.search.query.includes('during:');

  const [selectedHints, nonSelectedHints] = partition(
    allHints,
    (hint) =>
      (isSearchStaticModifierHintType(hint.type) && hint.values.length > 0) ||
      (hint.type === SearchModifierHintTypesEnum.MODIFIER && hint.values.every((e) => e.isSelected)) ||
      (hint.type === SearchStaticModifierHintTypesEnum.IN && isInSelected) ||
      (hint.type === SearchStaticModifierHintTypesEnum.IS && isStatusSelected) ||
      (hint.type === SearchStaticModifierHintTypesEnum.TAG && isTagSelected) ||
      (hint.key === ModifiersEnum.CUSTOM_FIELD && isDateSelected),
  );

  return [
    // Search filters hint should always be displayed first.
    searchFiltersHint,
    // Prepend all Other hints.
    ...selectedHints,
    ...nonSelectedHints,
  ];
}
